import React, { useEffect, useState } from 'react';
import { apiFetch } from '../api';
import CurveDatasetBuilder from '../components/CurveDatasetBuilder';
import DatasetTable from '../components/DatasetTable';
import HomographyViewer from '../components/HomographyViewer';
import KeypointAnnotator from '../components/KeypointAnnotator';

interface Dataset {
    [key: string]: any;
}

type Tab = 'datasets' | 'curve-builder' | 'homography' | 'keypoints';

const Training: React.FC = () => {
    const [activeTab, setActiveTab] = useState<Tab>('datasets');
    const [datasets, setDatasets] = useState<Dataset[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchDatasets = async () => {
        setLoading(true);
        try {
            const response = await apiFetch('/train/datasets');
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            data.sort((a: Dataset, b: Dataset) => {
                const dateA = a.annotated_at ? new Date(a.annotated_at).getTime() : 0;
                const dateB = b.annotated_at ? new Date(b.annotated_at).getTime() : 0;
                return dateB - dateA;
            });
            setDatasets(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load datasets');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDatasets();
    }, []);

    const tabStyle = (tab: Tab) => ({
        padding: '10px 16px',
        cursor: 'pointer',
        border: 'none',
        borderBottom: activeTab === tab ? '3px solid #2563eb' : '3px solid transparent',
        backgroundColor: 'transparent',
        fontWeight: activeTab === tab ? 'bold' : 'normal',
        fontSize: '14px',
    });

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <h1 style={{ marginTop: 0 }}>Training</h1>
            <div style={{ display: 'flex', gap: '4px', marginBottom: '20px' }}>
                <button style={tabStyle('datasets')} onClick={() => setActiveTab('datasets')}>Datasets</button>
                <button style={tabStyle('curve-builder')} onClick={() => setActiveTab('curve-builder')}>Curve Builder</button>
                <button style={tabStyle('homography')} onClick={() => setActiveTab('homography')}>Homography</button>
                <button style={tabStyle('keypoints')} onClick={() => setActiveTab('keypoints')}>Keypoint Annotator</button>
            </div>

            <div style={{ flex: 1, overflow: 'hidden' }}>
                {activeTab === 'datasets' && (
                    <>
                        {loading && <div>Loading...</div>}
                        {error && <div style={{ color: 'red' }}>Error: {error}</div>}
                        {!loading && !error && <DatasetTable data={datasets} onRefresh={fetchDatasets} />}
                    </>
                )}
                {activeTab === 'curve-builder' && <CurveDatasetBuilder />}
                {activeTab === 'homography' && <HomographyViewer />}
                {activeTab === 'keypoints' && <KeypointAnnotator />}
            </div>
        </div>
    );
};

export default Training;
